// Sales: quotations (Stage 2E-2), invoices (2E-3), clients, service catalogue and settings (2E-1). Admin only (the database enforces it).
// An operational business module. Its only link to Accounts is the Sales → Accounts payment sync (024, owner decision 8 Oct 2026).
import { useState, useEffect, useCallback, useMemo } from "react";
import { PageHeader, Tabs, Notice, SkeletonTable, SkeletonCard } from "../ui/kit/components";
import { makeCalendar } from "../lib/bs";
import { loadSales, errText } from "./api";
import SalesSettings from "./SalesSettings";
import Services from "./Services";
import Clients from "./Clients";
import Quotations from "./Quotations";
import Invoices from "./Invoices";
import SalesSync from "./SalesSync";

export default function SalesPage() {
    const [raw, setRaw] = useState(null);
    const [err, setErr] = useState("");
    const [tab, setTab] = useState(() => { try { return localStorage.getItem("sales.tab") || "quotations"; } catch { return "quotations"; } });
    const reload = useCallback(async () => {
        try { setErr(""); setRaw(await loadSales()); } catch (e) { setErr(errText(e)); }
    }, []);
    useEffect(() => { reload(); }, [reload]);
    const [focusInvoice, setFocusInvoice] = useState(null);           // open an invoice from a quotation link
    const go = (t) => { setTab(t); try { localStorage.setItem("sales.tab", t); } catch { /* private mode */ } };
    const data = useMemo(() => (raw ? { ...raw, calendar: makeCalendar(raw.calendar) } : null), [raw]);

    const activeClients = data ? data.clients.filter(c => c.active).length : undefined;
    const openQuotes = data ? data.quotations.filter(q => ["draft", "sent", "accepted"].includes(q.status)).length : undefined;
    const openInvoices = data ? data.invoices.filter(v => v.status === "draft" || (v.status === "issued" && v.payment_status !== "paid")).length : undefined;
    return (
        <div style={{ maxWidth: 1200 }}>
            <PageHeader title="Sales" subtitle="Quotations, invoices, clients, services and the company details used on them." />
            {err && <div style={{ marginBottom: 16 }}><Notice tone="danger">{err}</Notice></div>}
            <Tabs value={tab} onChange={go} items={[["quotations", "Quotations", openQuotes], ["invoices", "Invoices", openInvoices], ["clients", "Clients", activeClients], ["services", "Services"], ["sync", "Accounts sync"], ["settings", "Settings"]]} />
            {!data && !err ? (tab === "settings" ? <SkeletonCard lines={6} /> : <SkeletonTable rows={6} cols={4} />)
                : !data ? null
                : tab === "quotations" ? <Quotations data={data} reload={reload} onOpenInvoice={(id) => { setFocusInvoice(id); go("invoices"); }} />
                : tab === "invoices" ? <Invoices data={data} reload={reload} focusId={focusInvoice} onFocused={() => setFocusInvoice(null)} />
                : tab === "clients" ? <Clients clients={data.clients} reload={reload} data={data} />
                : tab === "services" ? <Services services={data.services} reload={reload} />
                : tab === "sync" ? <SalesSync data={data} />
                : <SalesSettings settings={data.settings} series={data.series} reload={reload} />}
        </div>
    );
}
